import { CircleAlert } from 'lucide-react';
import { useId, useMemo, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { FieldContext, type FieldContextValue } from '../lib/field-context';
import { Label } from './Label';

export interface FormFieldProps {
  label: ReactNode;
  /** The control. An `<Input>` wires itself up to this field automatically. */
  children: ReactNode;
  /** Helper text rendered under the label and referenced by the control. */
  description?: ReactNode;
  /** When set, the field renders as invalid and announces the message. */
  error?: ReactNode;
  required?: boolean;
  /** Override the generated control id (rarely needed). */
  id?: string;
  className?: string;
}

/**
 * Label + control + description + error, with every id and aria attribute
 * wired for you. The control reads them off context, so the markup stays flat:
 *
 *     <FormField label="Email" error={errors.email}>
 *       <Input type="email" name="email" />
 *     </FormField>
 */
export function FormField({
  label,
  children,
  description,
  error,
  required = false,
  id,
  className,
}: FormFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? `wend-field-${generatedId}`;
  const descriptionId = description ? `${fieldId}-description` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  const context = useMemo<FieldContextValue>(() => {
    const describedBy = [descriptionId, errorId].filter(Boolean).join(' ');
    return {
      id: fieldId,
      describedBy: describedBy === '' ? undefined : describedBy,
      invalid: Boolean(error),
    };
  }, [fieldId, descriptionId, errorId, error]);

  return (
    <FieldContext.Provider value={context}>
      <div className={cn('flex flex-col gap-2', className)}>
        <Label htmlFor={fieldId}>
          {label}
          {required && (
            <>
              <span aria-hidden="true" className="ml-1 text-brand-b">
                *
              </span>
              <span className="sr-only"> (required)</span>
            </>
          )}
        </Label>

        {description && (
          <p id={descriptionId} className="-mt-0.5 text-[0.8rem] leading-snug text-muted">
            {description}
          </p>
        )}

        {children}

        {error && (
          // role="alert" is announced on insert, so the message is read out the
          // moment validation fails without needing a permanent live region.
          <p
            id={errorId}
            role="alert"
            className="flex items-start gap-1.5 font-mono text-[0.72rem] leading-snug tracking-[0.04em] text-bad"
          >
            <CircleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}
